/**
 * Funnel Analytics Tab (MA-UI-023 + MA-UI-046)
 * 
 * Displays funnel-specific analytics metrics with real data.
 * Shows proper empty states when data is unavailable.
 * Includes AI Optimization suggestions based on funnel performance.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, 
  CheckCircle2, 
  Calendar,
  Clock,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  DollarSign,
  GitBranch,
  Sparkles,
  Lightbulb,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import OutcomeDistribution from '@/components/warroom/OutcomeDistribution';

// =============================================================================
// METRIC CARD COMPONENT
// =============================================================================

function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'indigo', emptyState }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    red: 'text-red-400 bg-red-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };

  const isEmptyOrZero = value === null || value === undefined || value === 0 || value === '0' || value === '0%' || value === '$0';

  return (
    <Card className="glass border-white/10">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-200/60 mb-1">{title}</p>
            {isEmptyOrZero && emptyState ? (
              <p className="text-lg text-slate-200/40 italic">{emptyState}</p>
            ) : (
              <p className="text-3xl font-bold text-slate-100">{value}</p>
            )}
            {subtitle && (
              <p className="text-sm text-slate-200/60 mt-1">{subtitle}</p>
            )}
            {trend !== undefined && trend !== null && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trend >= 0 ? '+' : ''}{trendValue}% vs prev period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// API HOOKS
// =============================================================================

function useFunnelDrilldown(funnelId, period) {
  return useQuery({
    queryKey: ['funnel-drilldown', funnelId, period],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      const res = await api.get(`/funnel-analytics/funnels/${funnelId}/drilldown?${params}`);
      return res.data;
    },
    enabled: !!funnelId,
    staleTime: 60000,
  });
}

function useFunnelOptimization(funnelId, period) {
  return useQuery({
    queryKey: ['funnel-optimization', funnelId, period],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      const res = await api.get(`/funnels/${funnelId}/optimize?${params}`);
      return res.data;
    },
    enabled: !!funnelId,
    staleTime: 120000, // 2 min cache
    retry: 1,
  });
}

// Step Economics Hook (MA-AN-053)
function useStepEconomics(funnelId, period) {
  return useQuery({
    queryKey: ['step-economics', funnelId, period],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      const res = await api.get(`/funnel-analytics/funnels/${funnelId}/economics?${params}`);
      return res.data;
    },
    enabled: !!funnelId,
    staleTime: 120000, // 2 min cache
    retry: 1,
  });
}

// =============================================================================
// AI OPTIMIZATION SECTION (MA-UI-046)
// =============================================================================

function OptimizationSuggestionCard({ suggestion, onReviewInBuilder }) {
  const severityColors = {
    high: 'bg-red-500/10 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    low: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  };
  
  return (
    <div className="p-4 bg-white/[0.03] rounded-lg border border-white/[0.06] hover:border-white/[0.12] transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-200">{suggestion.issue}</span>
        </div>
        <Badge variant="outline" className={severityColors[suggestion.severity] || severityColors.medium}>
          {suggestion.severity}
        </Badge>
      </div>
      
      <p className="text-xs text-slate-400 mb-3 pl-6">{suggestion.why_it_matters}</p>
      
      <div className="pl-6 p-2 bg-indigo-500/5 rounded border border-indigo-500/10">
        <p className="text-xs font-medium text-indigo-400 mb-1">Recommendation</p>
        <p className="text-sm text-slate-300">{suggestion.recommendation}</p>
      </div>
      
      {suggestion.step_id && (
        <div className="mt-3 pl-6">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 h-7 px-2"
            onClick={() => onReviewInBuilder?.(suggestion.step_id, suggestion.step_label)}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Review in Builder
          </Button>
        </div>
      )}
    </div>
  );
}

function AIOptimizationSection({ funnelId, period, onNavigateToBuilder }) {
  const { data: optimization, isLoading, error, refetch, isFetching } = useFunnelOptimization(funnelId, period);
  
  const handleReviewInBuilder = (stepId, stepLabel) => {
    if (onNavigateToBuilder) {
      onNavigateToBuilder(stepId, stepLabel);
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            AI Optimization Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
            <span className="text-slate-400">Analyzing funnel performance...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            AI Optimization Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-slate-400">Unable to generate optimization suggestions</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Empty/insufficient data state
  if (!optimization?.has_sufficient_data) {
    return (
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            AI Optimization Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Sparkles className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 mb-1">Not enough funnel activity yet</p>
            <p className="text-sm text-slate-500">
              {optimization?.summary || "Need more session data to generate optimization suggestions."}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const { suggestions, optimization_score, summary } = optimization;
  const highCount = suggestions?.filter(s => s.severity === 'high').length || 0;
  const mediumCount = suggestions?.filter(s => s.severity === 'medium').length || 0;
  
  return (
    <Card className="glass border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            AI Optimization Suggestions
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-slate-400 hover:text-slate-300"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {optimization_score !== null && (
          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-400">Optimization Score</span>
                <span className={`font-medium ${
                  optimization_score >= 80 ? 'text-emerald-400' :
                  optimization_score >= 60 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {optimization_score}/100
                </span>
              </div>
              <Progress 
                value={optimization_score} 
                className="h-1.5" 
              />
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Summary */}
        {summary && (
          <p className="text-sm text-slate-400 mb-4">{summary}</p>
        )}
        
        {/* Issue count badges */}
        {suggestions && suggestions.length > 0 && (
          <div className="flex gap-2 mb-4">
            {highCount > 0 && (
              <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
                {highCount} high priority
              </Badge>
            )}
            {mediumCount > 0 && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                {mediumCount} medium priority
              </Badge>
            )}
          </div>
        )}
        
        {/* Suggestions list */}
        {suggestions && suggestions.length > 0 ? (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {suggestions.map((suggestion) => (
              <OptimizationSuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onReviewInBuilder={handleReviewInBuilder}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">Your funnel is performing well!</p>
            <p className="text-sm text-slate-500">No major optimization issues detected.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// STEP ECONOMICS SECTION (MA-AN-053)
// =============================================================================

function StepEconomicsSection({ funnelId, period }) {
  const { data: economics, isLoading, error } = useStepEconomics(funnelId, period);
  
  if (isLoading) {
    return (
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <DollarSign className="w-5 h-5 text-purple-400" />
            Step Economics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
            <span className="text-slate-400">Calculating step economics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error || !economics) {
    return null; // Silently fail - this is an enhancement
  }
  
  const { step_economics, has_revenue_assumptions, revenue_assumptions_source, total_revenue, overall_revenue_retention } = economics;
  
  if (!step_economics || step_economics.length === 0) {
    return null;
  }
  
  // Classification color mapping
  const classificationColors = {
    filtering: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    friction: 'bg-red-500/10 text-red-400 border-red-500/30',
    value_add: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    neutral: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    exit: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
  };
  
  const classificationLabels = {
    filtering: 'Filtering',
    friction: 'Friction',
    value_add: 'Value Add',
    neutral: 'Neutral',
    exit: 'Exit',
  };
  
  return (
    <Card className="glass border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
            <DollarSign className="w-5 h-5 text-purple-400" />
            Step Economics
            <Badge variant="outline" className="ml-2 text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
              MA-AN-053
            </Badge>
          </CardTitle>
          <div className="text-right">
            <p className="text-sm text-slate-400">Overall Revenue Retention</p>
            <p className={`text-xl font-bold ${overall_revenue_retention >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {overall_revenue_retention?.toFixed(1) || 0}%
            </p>
          </div>
        </div>
        <CardDescription className="text-slate-200/60">
          Economic metrics per step • {has_revenue_assumptions ? `Using ${revenue_assumptions_source} settings` : 'Using default revenue assumptions'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <span className="text-slate-400">Filtering (qualification)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-400"></div>
            <span className="text-slate-400">Friction (problematic)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
            <span className="text-slate-400">Value Add</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-slate-500"></div>
            <span className="text-slate-400">Exit Node</span>
          </div>
        </div>
        
        {/* Step Economics Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="pb-3 text-slate-400 font-medium">Step</th>
                <th className="pb-3 text-slate-400 font-medium text-right">Reached</th>
                <th className="pb-3 text-slate-400 font-medium text-right">Passed</th>
                <th className="pb-3 text-slate-400 font-medium text-right">Pass Rate</th>
                <th className="pb-3 text-slate-400 font-medium text-right">Drop-Off</th>
                <th className="pb-3 text-purple-400 font-medium text-right">Est. Revenue</th>
                <th className="pb-3 text-purple-400 font-medium text-right">Profit/Step</th>
                <th className="pb-3 text-purple-400 font-medium text-right">Rev. Retention</th>
                <th className="pb-3 text-purple-400 font-medium text-right">Value Lift</th>
                <th className="pb-3 text-slate-400 font-medium text-center">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {step_economics.map((step) => (
                <tr key={step.step_id} className="hover:bg-white/[0.02]">
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs text-slate-500 shrink-0">
                        {step.step_position}
                      </Badge>
                      <span className="text-slate-200 truncate max-w-[200px]" title={step.step_label}>
                        {step.step_label || step.step_id}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 text-right text-slate-300">{step.reached?.toLocaleString() || 0}</td>
                  <td className="py-3 text-right text-slate-300">{step.passed?.toLocaleString() || 0}</td>
                  <td className="py-3 text-right text-slate-300">{step.pass_rate?.toFixed(1) || 0}%</td>
                  <td className={`py-3 text-right ${step.drop_off_rate > 35 ? 'text-red-400' : 'text-slate-300'}`}>
                    {step.drop_off_rate?.toFixed(1) || 0}%
                  </td>
                  <td className="py-3 text-right text-purple-300 font-medium">
                    ${step.attributed_revenue?.toLocaleString() || 0}
                  </td>
                  <td className="py-3 text-right text-purple-300 font-medium">
                    {step.profit_per_step !== null ? `$${step.profit_per_step?.toFixed(0)}` : '—'}
                  </td>
                  <td className={`py-3 text-right font-medium ${
                    step.revenue_retention >= 70 ? 'text-emerald-400' :
                    step.revenue_retention >= 50 ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {step.revenue_retention !== null ? `${step.revenue_retention?.toFixed(1)}%` : '—'}
                  </td>
                  <td className={`py-3 text-right font-medium ${
                    step.value_lift >= 1.2 ? 'text-emerald-400' :
                    step.value_lift < 0.9 ? 'text-red-400' : 'text-slate-300'
                  }`}>
                    {step.value_lift !== null ? `${step.value_lift?.toFixed(2)}×` : '—'}
                  </td>
                  <td className="py-3 text-center">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${classificationColors[step.step_classification] || classificationColors.neutral}`}
                    >
                      {classificationLabels[step.step_classification] || 'Neutral'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Formula Documentation */}
        <div className="mt-4 p-3 bg-white/[0.02] rounded-lg border border-white/5">
          <p className="text-xs text-slate-500 mb-2 font-medium">Formulas (V1 Attribution Model)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-400">
            <div><span className="text-purple-400">Profit/Step</span> = Revenue ÷ Reached</div>
            <div><span className="text-purple-400">Rev. Retention</span> = Remaining ÷ Potential</div>
            <div><span className="text-purple-400">Value Lift</span> = After ÷ Before</div>
            <div className="text-slate-500">Attribution: Pass-through to all steps</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function FunnelAnalyticsTab({ funnelId, funnelName, onNavigateToStep }) {
  const [period, setPeriod] = React.useState('30d');
  
  const { data: drilldown, isLoading, error } = useFunnelDrilldown(funnelId, period);
  
  // Handle "Review in Builder" click from optimization suggestions
  const handleNavigateToBuilder = (stepId, stepLabel) => {
    if (onNavigateToStep) {
      onNavigateToStep(stepId, stepLabel);
    }
  };
  
  // Calculate estimated revenue (placeholder until revenue assumptions implemented)
  const qualifiedCount = drilldown?.qualified_count || 0;
  const estimatedRevenuePerLead = null; // Would come from funnel or vertical settings
  const estimatedRevenue = estimatedRevenuePerLead ? qualifiedCount * estimatedRevenuePerLead : null;
  
  // Calculate rates
  const totalSessions = drilldown?.total_sessions || 0;
  const completedSessions = drilldown?.completed_sessions || 0;
  const completionRate = totalSessions > 0 ? ((completedSessions / totalSessions) * 100).toFixed(1) : 0;
  const bookingRate = drilldown?.booking_rate || 0;
  const qualificationRate = totalSessions > 0 ? ((qualifiedCount / totalSessions) * 100).toFixed(1) : 0;
  const dropOffRate = totalSessions > 0 ? (100 - parseFloat(completionRate)).toFixed(1) : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-200/60">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    // Check if it's a 404 (funnel not found) or actual API error
    const is404 = error?.response?.status === 404;
    const isNetworkError = !error?.response;
    
    return (
      <div className="p-6" data-testid="funnel-analytics-tab">
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-200/40 mb-4" />
            <h3 className="text-lg font-medium text-slate-200/60 mb-2">
              {is404 
                ? "Funnel not found" 
                : isNetworkError 
                  ? "Unable to connect to analytics service"
                  : "No funnel session data available yet"
              }
            </h3>
            <p className="text-sm text-slate-200/40">
              {is404 
                ? "This funnel may have been deleted or moved."
                : isNetworkError 
                  ? "Please check your connection and try again."
                  : "Session analytics will appear here once visitors start interacting with this funnel."
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasSessionData = totalSessions > 0;

  return (
    <div className="space-y-6 p-6" data-testid="funnel-analytics-tab">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Funnel Analytics</h2>
          <p className="text-slate-200/60 text-sm mt-1">Performance metrics for {funnelName}</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="glass border-white/10">
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="90d">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Sessions"
          value={hasSessionData ? totalSessions.toLocaleString() : 0}
          icon={Users}
          color="indigo"
          emptyState="No sessions yet"
        />
        <MetricCard
          title="Completion Rate"
          value={hasSessionData ? `${completionRate}%` : '0%'}
          subtitle={hasSessionData ? `${completedSessions} completed` : null}
          icon={CheckCircle2}
          color="green"
          emptyState="No data"
        />
        <MetricCard
          title="Booking Rate"
          value={hasSessionData ? `${bookingRate}%` : '0%'}
          icon={Calendar}
          color="blue"
          emptyState="No booking data"
        />
        <MetricCard
          title="Qualification Rate"
          value={hasSessionData ? `${qualificationRate}%` : '0%'}
          subtitle={hasSessionData ? `${qualifiedCount} qualified` : null}
          icon={Target}
          color="amber"
          emptyState="No data"
        />
        <MetricCard
          title="Estimated Revenue"
          value={estimatedRevenue !== null ? `$${estimatedRevenue.toLocaleString()}` : null}
          icon={DollarSign}
          color="purple"
          emptyState="Revenue assumptions required"
        />
      </div>

      {/* Empty State Message */}
      {!hasSessionData && (
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-200/40 mb-4" />
            <h3 className="text-lg font-medium text-slate-200/60 mb-2">No funnel session data yet</h3>
            <p className="text-sm text-slate-200/40">
              Session analytics will appear here once visitors start interacting with this funnel.
            </p>
          </CardContent>
        </Card>
      )}

      {/* AI Optimization Suggestions (MA-UI-046) */}
      <AIOptimizationSection 
        funnelId={funnelId} 
        period={period} 
        onNavigateToBuilder={handleNavigateToBuilder}
      />

      {/* Step Economics Section (MA-AN-053) */}
      {hasSessionData && (
        <StepEconomicsSection funnelId={funnelId} period={period} />
      )}

      {/* MA-ANALYTICS-OUTCOME-DASH-005: Outcome Distribution + Performance Summary */}
      {hasSessionData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Outcome Distribution Widget */}
          <OutcomeDistribution
            period={period}
            funnelId={funnelId}
            compact
          />

          {/* Performance Summary */}
          <Card className="glass border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-100">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-200/60">Completion Rate</span>
                    <span className="text-slate-100">{completionRate}%</span>
                  </div>
                  <Progress value={parseFloat(completionRate)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-200/60">Booking Rate</span>
                    <span className="text-slate-100">{bookingRate}%</span>
                  </div>
                  <Progress value={bookingRate} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-200/60">Qualification Rate</span>
                    <span className="text-slate-100">{qualificationRate}%</span>
                  </div>
                  <Progress value={parseFloat(qualificationRate)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-200/60">Drop-Off Rate</span>
                    <span className="text-slate-100">{dropOffRate}%</span>
                  </div>
                  <Progress value={parseFloat(dropOffRate)} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Funnel Step Performance - Only show if data exists */}
      {hasSessionData && drilldown?.step_conversions?.length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-100">
              <GitBranch className="w-5 h-5 text-indigo-400" />
              Funnel Step Performance
            </CardTitle>
            <CardDescription className="text-slate-200/60">
              Conversion metrics at each funnel step
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {drilldown.step_conversions.map((step, index) => (
                <div key={step.node_id} className="bg-white/5 rounded-lg p-4 ring-1 ring-white/10">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs text-slate-200/60">
                          Step {index + 1}
                        </Badge>
                        <span className="text-sm text-slate-200/60">{step.node_type}</span>
                      </div>
                      <p className="text-slate-100 mt-1">{step.node_label || step.node_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-100">{step.conversion_rate}%</p>
                      <p className="text-sm text-slate-200/60">completion</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-200/60">
                    <span>{step.views} sessions</span>
                    <span>→</span>
                    <span>{step.responses} completed</span>
                    <span className="text-slate-200/40">|</span>
                    <span className={step.drop_off_rate > 30 ? 'text-red-400' : 'text-slate-200/60'}>
                      {step.drop_off_rate}% drop-off
                    </span>
                    {step.drop_off_rate > 30 && (
                      <Badge variant="outline" className="ml-auto bg-red-500/10 text-red-400 border-red-500/30">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        High drop-off
                      </Badge>
                    )}
                  </div>
                  <Progress value={step.conversion_rate} className="h-2 mt-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Insight Panel */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <DollarSign className="w-5 h-5 text-purple-400" />
            Revenue Insight
          </CardTitle>
        </CardHeader>
        <CardContent>
          {estimatedRevenue !== null ? (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white/5 rounded-lg ring-1 ring-white/10">
                <p className="text-3xl font-bold text-slate-100">${estimatedRevenue.toLocaleString()}</p>
                <p className="text-sm text-slate-200/60 mt-1">Estimated Revenue</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg ring-1 ring-white/10">
                <p className="text-3xl font-bold text-slate-100">{qualifiedCount}</p>
                <p className="text-sm text-slate-200/60 mt-1">Qualified Leads</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg ring-1 ring-white/10">
                <p className="text-3xl font-bold text-slate-100">${estimatedRevenuePerLead || 0}</p>
                <p className="text-sm text-slate-200/60 mt-1">Rev per Qualified Lead</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <DollarSign className="w-10 h-10 mx-auto text-slate-200/40 mb-3" />
              <p className="text-slate-200/60 mb-2">Revenue estimation unavailable</p>
              <p className="text-sm text-slate-200/40">
                Estimated revenue requires revenue assumptions or qualifying event mappings to be configured.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
